/**
 * Done. CPD — Landing Learn Library Section (Stitch v2 / screen 5)
 *
 * Public-side preview of the module library. Renders a static 9-card grid
 * with category filters and a search input. The full auth-gated library
 * (wired to GET /api/modules) lives in a future LearnLibraryPage; this
 * section is the marketing teaser that funnels into signup.
 */

import { useMemo, useState } from 'react';

type Category =
  | 'All Topics'
  | 'Required'
  | 'Core'
  | 'Science'
  | 'Elective'
  | 'Foundation'
  | 'Wellness'
  | 'Performance'
  | 'New';

interface Topic {
  icon: string;
  title: string;
  desc: string;
  tag: Category;
}

const TOPICS: Topic[] = [
  { icon: 'child_care',              title: 'Pediatrics',    tag: 'Required',    desc: 'Specialised protocols for developmental milestones and pediatric spinal health assessments.' },
  { icon: 'hardware',                title: 'Techniques',    tag: 'Core',        desc: 'Advanced Diversified, Gonstead, and SOT refinement through high-definition video masterclasses.' },
  { icon: 'settings_accessibility',  title: 'Biomechanics',  tag: 'Science',     desc: 'In-depth analysis of kinetic chains and functional movement patterns in clinical diagnosis.' },
  { icon: 'elderly',                 title: 'Geriatrics',    tag: 'Elective',    desc: 'Gentle force techniques and co-management strategies for ageing spinal conditions.' },
  { icon: 'skeleton',                title: 'Anatomy',       tag: 'Foundation',  desc: '3D cadaveric reviews and functional neuro-anatomy of the spinal column and periphery.' },
  { icon: 'psychology_alt',          title: 'Philosophy',    tag: 'Wellness',    desc: 'Merging the vitalistic paradigm with modern evidence-based chiropractic models.' },
  { icon: 'sports_gymnastics',       title: 'Sports',        tag: 'Performance', desc: 'Acute injury management and athletic performance optimisation protocols.' },
  { icon: 'menu_book',               title: 'Research',      tag: 'New',         desc: 'Critical appraisal of recent clinical trials and systematic reviews in musculoskeletal health.' },
  { icon: 'neurology',               title: 'Neuroscience',  tag: 'Core',        desc: 'The neuro-physiological effects of spinal manipulation on the central nervous system.' },
];

const CATEGORIES: Category[] = ['All Topics', 'Required', 'Core', 'Science', 'Elective', 'Foundation', 'Wellness', 'Performance', 'New'];

const TAG_STYLE: Record<Category, string> = {
  'All Topics':  '',
  Required:      'bg-[var(--stitch-tertiary-fixed)] text-[var(--stitch-on-tertiary-fixed-variant)]',
  New:           'bg-[var(--stitch-tertiary-fixed)] text-[var(--stitch-on-tertiary-fixed-variant)]',
  Core:          'bg-[var(--stitch-surface-container-high)] text-[var(--stitch-secondary)]',
  Science:       'bg-[var(--stitch-surface-container-high)] text-[var(--stitch-secondary)]',
  Elective:      'bg-[var(--stitch-surface-container-high)] text-[var(--stitch-secondary)]',
  Foundation:    'bg-[var(--stitch-surface-container-high)] text-[var(--stitch-secondary)]',
  Wellness:      'bg-[var(--stitch-surface-container-high)] text-[var(--stitch-secondary)]',
  Performance:   'bg-[var(--stitch-surface-container-high)] text-[var(--stitch-secondary)]',
};

export function LearnLibrary(): React.ReactElement {
  const [query, setQuery] = useState('');
  const [activeCat, setActiveCat] = useState<Category>('All Topics');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return TOPICS.filter(t =>
      (activeCat === 'All Topics' || t.tag === activeCat) &&
      (!q || t.title.toLowerCase().includes(q) || t.desc.toLowerCase().includes(q))
    );
  }, [query, activeCat]);

  return (
    <section
      id="section-learn"
      className="py-20 md:py-24 bg-[#f9f9f7]"
    >
      <div
        className="mx-auto px-4 md:px-10"
        style={{ maxWidth: 'var(--stitch-container-max)' }}
      >
        <div className="mb-10 max-w-3xl">
          <h2 className="stitch-font-display text-[40px] md:text-[56px] font-bold text-[var(--stitch-primary)] mb-6 tracking-tight uppercase">
            Learn.
          </h2>
          <p className="stitch-font-body text-[17px] text-[var(--stitch-on-surface-variant)] leading-relaxed mb-3">
            Elevate your clinical practice through our multi-modal learning ecosystem.
            Evidence-based research, practical technique demonstrations, and neurological
            insights — designed specifically for the modern chiropractor.
          </p>
          <p className="text-sm italic text-[var(--stitch-secondary)] font-medium flex items-center gap-2">
            <span className="material-symbols-outlined text-[18px]">info</span>
            Your recommended modules will be highlighted once you complete the survey.
          </p>
        </div>

        {/* Filter + search */}
        <div className="mb-10 flex flex-col md:flex-row gap-4 items-center justify-between bg-[var(--stitch-surface-container-low)] p-4 rounded-xl border border-[var(--stitch-outline-variant)]">
          <div className="relative w-full md:w-96">
            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-[var(--stitch-on-surface-variant)]">search</span>
            <input
              type="text"
              value={query}
              onChange={e => { setQuery(e.target.value); }}
              placeholder="Search content library…"
              className="w-full pl-10 pr-4 py-2 bg-[var(--stitch-surface-container-lowest)] border border-[var(--stitch-outline-variant)] rounded-lg focus:ring-1 focus:ring-[var(--stitch-primary)] focus:border-[var(--stitch-primary)] outline-none stitch-font-body text-sm"
              aria-label="Search content library"
            />
          </div>
          <div className="flex items-center gap-2 overflow-x-auto w-full md:w-auto" role="tablist" aria-label="Filter by category">
            <span className="stitch-font-label text-[13px] text-[var(--stitch-on-surface-variant)] whitespace-nowrap mr-2 normal-case tracking-normal">
              Filter by:
            </span>
            {CATEGORIES.map(cat => {
              const active = cat === activeCat;
              return (
                <button
                  key={cat}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => { setActiveCat(cat); }}
                  className={`px-4 py-1.5 rounded-full stitch-font-label text-[13px] whitespace-nowrap normal-case tracking-normal transition-colors ${
                    active
                      ? 'bg-[var(--stitch-primary)] text-[var(--stitch-on-primary)]'
                      : 'bg-[var(--stitch-surface-container-highest)] text-[var(--stitch-on-surface-variant)] hover:bg-[var(--stitch-outline-variant)]'
                  }`}
                >
                  {cat}
                </button>
              );
            })}
          </div>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map(t => (
            <article
              key={t.title}
              className="bg-[var(--stitch-surface-container-lowest)] border border-[var(--stitch-outline-variant)] hover:border-[var(--stitch-primary)] rounded-xl p-8 stitch-ghost-shadow transition-all duration-300 flex flex-col gap-5 group hover:-translate-y-1"
            >
              <div className="flex justify-between items-start">
                <span className="material-symbols-outlined text-[var(--stitch-primary)] text-4xl group-hover:scale-110 transition-transform">
                  {t.icon}
                </span>
                <span className={`px-3 py-1 stitch-font-label text-[12px] rounded-full ring-1 ring-[var(--stitch-outline-variant)]/30 ${TAG_STYLE[t.tag]}`}>
                  {t.tag.toUpperCase()}
                </span>
              </div>
              <div>
                <h3 className="stitch-font-headline text-[22px] text-[var(--stitch-primary)] mb-2 uppercase">{t.title}</h3>
                <p className="stitch-font-body text-[15px] text-[var(--stitch-on-surface-variant)]">{t.desc}</p>
              </div>
              <div className="mt-auto pt-4 border-t border-[var(--stitch-outline-variant)] flex items-center justify-between text-[var(--stitch-primary)] stitch-font-label text-[13px] group-hover:gap-2 transition-all">
                <span>Explore Modules</span>
                <span className="material-symbols-outlined">arrow_forward</span>
              </div>
            </article>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full text-center py-12 text-[var(--stitch-on-surface-variant)]">
              No modules match your filter.
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
